import { Group, Modal, type ModalProps } from "@mantine/core";
import {
  IconAlertTriangleFilled,
  IconCircleCheckFilled
} from "@tabler/icons-react";
import classNames from "classnames";
import { ModalFooter } from "../../../../../mantine/components/ModalFooter/ModalFooter";
import { THEME_VARS } from "../../../../../utils/constants";
import { getErrorText } from "../../../../../utils/get-error-text";
import type { BasicConfirmationFooterProps } from "./BasicConfirmationModalFooter/BasicConfirmationModalFooter";
import { BasicConfirmationFooter } from "./BasicConfirmationModalFooter/BasicConfirmationModalFooter";

export enum BasicConfirmationModalIcon {
  Error = "error",
  Success = "success",
  Warning = "warning"
}

type BasicConfirmationModalProps = Omit<ModalProps, "onClose"> &
  BasicConfirmationFooterProps & {
    error?: unknown;
    icon?: BasicConfirmationModalIcon;
  };

const modalIcons = {
  [BasicConfirmationModalIcon.Error]: (
    <IconAlertTriangleFilled
      className="error-icon"
      color={THEME_VARS.error[6]}
      role="img"
    />
  ),
  [BasicConfirmationModalIcon.Success]: (
    <IconCircleCheckFilled className="success-icon" color="teal" role="img" />
  ),
  [BasicConfirmationModalIcon.Warning]: (
    <IconAlertTriangleFilled
      className="warning-icon"
      color="var(--mantine-color-warning-filled)"
      role="img"
    />
  )
} as const;

function getModalIcon(iconType: BasicConfirmationModalIcon) {
  return modalIcons[iconType];
}

function BasicConfirmationModal({
  className,
  children,
  confirmationButtonColor = "secondary",
  confirmationButtonText = "OK",
  cancelButtonText = "Abbrechen",
  error,
  title,
  isConfirmationBtnDisabled,
  isLoading,
  icon,
  toggleModal,
  onConfirm,
  onCancel,
  ...props
}: BasicConfirmationModalProps) {
  function handleHeaderCloseBtn() {
    if (onCancel) {
      onCancel();
    }

    toggleModal();
  }

  const headerIcon = (function getHeaderIcon() {
    if (error) {
      return BasicConfirmationModalIcon.Error;
    } else if (icon) {
      return icon;
    } else if (confirmationButtonColor === "error") {
      return BasicConfirmationModalIcon.Error;
    } else if (confirmationButtonColor === "warning") {
      return BasicConfirmationModalIcon.Warning;
    }

    return undefined;
  })();

  const CommonFooter = (
    <BasicConfirmationFooter
      cancelButtonText={cancelButtonText}
      confirmationButtonColor={confirmationButtonColor}
      confirmationButtonText={confirmationButtonText}
      error={error}
      isConfirmationBtnDisabled={isConfirmationBtnDisabled}
      isLoading={isLoading}
      toggleModal={toggleModal}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );

  return (
    <Modal
      className={classNames("BasicConfirmationModal", className)}
      title={
        title ? (
          <Group gap="xs" wrap="nowrap">
            {headerIcon && getModalIcon(headerIcon)}
            {title}
          </Group>
        ) : undefined
      }
      withCloseButton={!!title}
      zIndex={201} // display over other modals (200)
      onClose={onCancel ? handleHeaderCloseBtn : toggleModal}
      {...props}
    >
      {error ? <div>{getErrorText(error)}</div> : <div>{children}</div>}
      <ModalFooter>{CommonFooter}</ModalFooter>
    </Modal>
  );
}

export { BasicConfirmationModal, BasicConfirmationModalProps };
